import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import GreetingHeader from "../../components/dashboard/GreetingHeader";
import StatCard from "../../components/dashboard/StatCard";
import EmptyHint from "../../components/dashboard/EmptyHint";
import QuickAction from "../../components/dashboard/QuickAction";
import MonthlyReportButton from "../../components/dashboard/MonthlyReportButton";
import { num, count, dayCount, plural, joinParts, NO_VALUE } from "../../lib/vocabulary";
import { formatISTDayLabel, todayIST } from "../../lib/datetime";
import PlannedDayCard from "../../components/tour-plans/PlannedDayCard";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import {
  CalendarDays,
  CheckCircle2,
  TrendingUp,
  Sun,
  Wallet,
  Plus,
  CalendarPlus,
  Receipt,
  CalendarRange,
} from "lucide-react";

export default function SalesOfficerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/sales_officer");
        if (!cancelled) setData(data);
      } catch (e) {
        // The rejection used to be swallowed by a bare `finally`. `data` stayed
        // null, `pending_expenses` fell back to `{count: 0}` and the Pending
        // Expenses tile read "0 · All clear" — a reassurance manufactured out
        // of a failed request.
        if (!cancelled) {
          setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const s = data?.stats || {};

  // Two types (P5/P8), and NO third number invented from them. CL and PL are
  // summed only when BOTH arrived: `(undefined ?? 0) + (undefined ?? 0)` is a
  // zero balance, which is a claim about how much leave this officer has left.
  const cl = s.leave_balance?.CL;
  const pl = s.leave_balance?.PL;
  //
  // AND THE SUM IS A DAY COUNT, not a bare JS number. `cl + pl` is the one
  // piece of arithmetic on this page the server did not do, and it was
  // rendered raw: CL 8 plus a PL balance of 1.13 — two decimals the
  // proportional LOP split produces honestly — put `9.129999999999999` in the
  // tile. `dayCount` drops the binary tail without touching a real half day.
  const leaveTotal =
    cl === undefined || cl === null || pl === undefined || pl === null
      ? undefined
      : dayCount(cl + pl);

  // Same rule for the expense tile: no `|| {count: 0, amount: 0}`.
  const pendingCount = s.pending_expenses?.count;
  const pendingAmount = s.pending_expenses?.amount;
  const pendingHelper =
    pendingCount === undefined || pendingCount === null
      ? "Not loaded"
      : pendingCount
        ? `₹${formatINR(pendingAmount)}`
        : "All clear";

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <GreetingHeader
        user={user}
        subtitle="Your day, your numbers. Drill into any tile, or log activity as you go from the field."
      />

      {error && (
        <div
          className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2"
          data-testid="dashboard-error"
        >
          Your figures did not load — {error}
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <QuickAction
          to="/doctor-visits/new"
          Icon={Plus}
          label="Add Visit"
          helper="Log a doctor / chemist visit"
          testId="quick-action-add-visit"
        />
        <QuickAction
          to="/leaves/apply"
          Icon={CalendarPlus}
          label="Apply Leave"
          helper="Casual or privilege leave — sickness files under CL"
          testId="quick-action-apply-leave"
        />
        <QuickAction
          to="/expenses/new"
          Icon={Receipt}
          label="Submit Expense"
          helper="Travel, food, marketing receipts"
          testId="quick-action-submit-expense"
        />
      </div>

      {/* The one-button monthly report — the same control on every dashboard. */}
      <MonthlyReportButton />

      {/* Every tile navigates (RC-15). All five were inert. */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          testId="stat-today-planned"
          label="Today's Plan"
          value={num(loading, s.today_planned)}
          helper="Doctors scheduled"
          Icon={CalendarDays}
          onClick={() => navigate("/route-planner")}
          loading={loading}
        />
        {/* PLANNED-VISITED, not a raw visit count (§1 Fix B). Four ad hoc
            calls used to read as a planned day executed. The unplanned
            number is shown beside it, separately — never folded in. Both
            come from the server (`day_progress_for`), the same function the
            day card below ticks from. */}
        <StatCard
          testId="stat-today-completed"
          label="Today Completed"
          value={num(loading, s.today_visited_planned)}
          helper={loading ? "…" : `Of plan · ${count(s.today_unplanned)} unplanned`}
          Icon={CheckCircle2}
          accent
          onClick={() => navigate("/doctor-visits")}
          loading={loading}
        />
        <StatCard
          testId="stat-month-visits"
          label="This Month"
          value={num(loading, s.month_visits)}
          helper="Visits completed"
          Icon={TrendingUp}
          onClick={() => navigate("/doctor-visits")}
          loading={loading}
        />
        {/* CL · PL, never a bare entitlement and never a fixed annual figure
            (P5/P8). `count()` renders a genuine 0 as 0 and a missing type as
            the dash, which the old `?? 0` collapsed into one. */}
        <StatCard
          testId="stat-leave-balance"
          label="Leave Balance"
          value={num(loading, leaveTotal)}
          helper={loading ? "…" : `CL ${count(cl)} · PL ${count(pl)}`}
          Icon={Sun}
          onClick={() => navigate("/leaves")}
          loading={loading}
        />
        <StatCard
          testId="stat-pending-expenses"
          label="Pending Expenses"
          value={num(loading, pendingCount)}
          helper={loading ? "…" : pendingHelper}
          Icon={Wallet}
          onClick={() => navigate("/expenses")}
          loading={loading}
        />
      </div>

      {/* Today's planned doctors, with Mark visited per row (§1 Fix B).
          `today_plan_id` is null with no approved plan this month, and the
          card says so instead of drawing an empty list. */}
      {!loading && !error && (
        <PlannedDayCard planId={s.today_plan_id || null} date={todayIST()} />
      )}

      {/* Upcoming tour plan */}
      <Card className="bg-white border-callisto shadow-sm rounded-xl">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base text-callisto-navy flex items-center gap-2">
            <CalendarRange className="h-4 w-4 text-callisto-gold" />
            My upcoming tour
          </CardTitle>
          <span className="text-[11px] uppercase tracking-wider text-callisto-muted">
            Next 3 days
          </span>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : data?.upcoming_tour?.length ? (
            <ul className="divide-y divide-callisto" data-testid="upcoming-tour">
              {data.upcoming_tour.map((t) => (
                <li key={t.id} className="py-3 flex items-center justify-between gap-3">
                  <div>
                    {/* One date format, in IST (RC-8). */}
                    <p className="text-sm font-semibold text-callisto-navy">
                      {formatISTDayLabel(t.date)}
                    </p>
                    <p className="text-xs text-callisto-muted">
                      {joinParts([t.summary]) || NO_VALUE}
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-callisto-gold">
                    {t.visit_count === undefined || t.visit_count === null
                      ? NO_VALUE
                      : plural(t.visit_count, "visit")}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyHint
              Icon={CalendarRange}
              title="No tour plan yet"
              hint="Once you build a beat plan in Route Planner, your next 3 days will preview here."
              cta={{ to: "/route-planner", label: "Open Route Planner" }}
              testId="upcoming-tour-empty"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// `QuickAction` moved to components/dashboard/QuickAction.jsx — the manager
// dashboard renders the same tile (§1 Fix A).

function formatINR(n) {
  if (n === null || n === undefined) return NO_VALUE;
  try {
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);
  } catch {
    return String(n);
  }
}
